import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { readStoredAuthSession, type StoredAuthSession } from '@delta/auth';
import type { PlatformLoginResponse, PlatformOperator } from '@delta/domain';
import { platformApi, platformAuthStorage } from '../lib/api';
import { clearSecureQueryCache, PLATFORM_SECURE_QUERY_ROOT } from '../shared/querySecurity';

interface PlatformAuthContextValue {
  token: string | null;
  operator: PlatformOperator | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const PlatformAuthContext = createContext<PlatformAuthContextValue | null>(null);

export function PlatformAuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<StoredAuthSession<PlatformOperator> | null>(
    () => readStoredAuthSession(platformAuthStorage),
  );
  const token = session?.token ?? null;
  const operator = session?.user ?? null;

  const login = useCallback(async (username: string, password: string) => {
    const { data } = await platformApi.post<PlatformLoginResponse>('/auth/login', { username, password });
    clearSecureQueryCache(queryClient, PLATFORM_SECURE_QUERY_ROOT);
    platformAuthStorage.setToken(data.accessToken);
    platformAuthStorage.setUser(data.operator);
    setSession({ token: data.accessToken, user: data.operator });
  }, [queryClient]);

  const logout = useCallback(() => {
    clearSecureQueryCache(queryClient, PLATFORM_SECURE_QUERY_ROOT);
    platformAuthStorage.clear();
    setSession(null);
  }, [queryClient]);

  useEffect(() => {
    const renewed = () => setSession(readStoredAuthSession(platformAuthStorage));
    window.addEventListener('delta:platform-unauthorized', logout);
    window.addEventListener('delta:platform-token-renewed', renewed);
    return () => {
      window.removeEventListener('delta:platform-unauthorized', logout);
      window.removeEventListener('delta:platform-token-renewed', renewed);
    };
  }, [logout]);

  const value = useMemo(() => ({ token, operator, login, logout }), [login, logout, operator, token]);
  return <PlatformAuthContext.Provider value={value}>{children}</PlatformAuthContext.Provider>;
}

export function usePlatformAuth() {
  const context = useContext(PlatformAuthContext);
  if (!context) throw new Error('usePlatformAuth 必须在 PlatformAuthProvider 内使用');
  return context;
}
